import {
  skipToken,
  useMutation,
  useQueries,
  useQuery,
  useQueryClient,
  useSuspenseQuery,
} from "@tanstack/react-query";

import {
  type GroundTruthIn,
  type ObservationsIn,
  perceptionClient,
  type RecentStill,
} from "./perceptionClient";

export const perceptionKeys = {
  phases: (sessionKey: string) => ["perception", sessionKey, "phases"] as const,
  clinicRoster: (sessionKey: string) =>
    ["perception", sessionKey, "clinic-roster"] as const,
  still: (sessionKey: string, roomId: string) =>
    ["perception", sessionKey, "still", roomId] as const,
  recentStills: (sessionKey: string, roomId: string) =>
    ["perception", sessionKey, "recent-stills", roomId] as const,
  video: (sessionKey: string, roomId: string, from: number | null) =>
    ["perception", sessionKey, "video", roomId, from] as const,
  groundTruth: (sessionKey: string, roomId: string) =>
    ["perception", sessionKey, "ground-truth", roomId] as const,
  events: (sessionKey: string, roomId: string) =>
    ["perception", sessionKey, "events", roomId] as const,
  slot: (sessionKey: string, roomId: string, slotId: string) =>
    ["perception", sessionKey, "slot", roomId, slotId] as const,
};

/**
 * A snapshot the reader refreshes by hand: each read signs the stills again
 * under new URLs, so a poll would reload the pictures being looked at.
 */
export const useRoomSlot = (
  sessionKey: string,
  roomId: string,
  slotId: string,
) =>
  useQuery({
    queryKey: perceptionKeys.slot(sessionKey, roomId, slotId),
    queryFn: ({ signal }) => perceptionClient.slot(roomId, slotId, signal),
    gcTime: 0,
    retry: false,
    refetchOnWindowFocus: false,
  });

/** The camera gateway records about one still a second per room. */
const STILL_POLL_MS = 2_000;

/**
 * Polls only while the tab is open (`active`) and the page is visible: React
 * Query skips interval refetches in a hidden document. No retry: the next tick
 * is the retry.
 */
const polling = (active: boolean) => ({
  enabled: active,
  refetchInterval: active ? STILL_POLL_MS : (false as const),
  refetchIntervalInBackground: false,
  staleTime: 0,
  gcTime: 0,
  retry: false,
});

/** An occupied room re-sights its people about every 30 s, so a faster poll shows nothing new. */
const CLINIC_ROSTER_POLL_MS = 15_000;

export const useClinicRoster = (sessionKey: string) =>
  useSuspenseQuery({
    queryKey: perceptionKeys.clinicRoster(sessionKey),
    queryFn: ({ signal }) => perceptionClient.clinicRoster(signal),
    refetchInterval: CLINIC_ROSTER_POLL_MS,
  });

export const useRoomPhases = (sessionKey: string, active: boolean) =>
  useQuery({
    queryKey: perceptionKeys.phases(sessionKey),
    queryFn: ({ signal }) => perceptionClient.phases(signal),
    ...polling(active),
  });

/** In `roomIds` order; the panel reads them to tell which rooms have sent a still. */
export const useRoomStills = (
  sessionKey: string,
  roomIds: readonly string[],
  active: boolean,
) =>
  useQueries({
    queries: roomIds.map((roomId) => ({
      queryKey: perceptionKeys.still(sessionKey, roomId),
      queryFn: ({ signal }: { signal: AbortSignal }) =>
        perceptionClient.still(roomId, signal),
      ...polling(active),
    })),
  });

/** brian's recent-stills links last 300 s; a refresh keeps a loaded still's URL, so the browser's cached image is reused, only while it is this young. */
const RELINK_AFTER_MS = 240_000;

/**
 * The last two minutes of a room's stills, read once: a refetch signs the stills
 * again under new URLs, so the reader refreshes by hand while scrubbing.
 */
export const useRecentStills = (
  sessionKey: string,
  roomId: string | null,
  enabled: boolean,
) =>
  useQuery({
    queryKey: perceptionKeys.recentStills(sessionKey, roomId ?? ""),
    queryFn: ({ signal }) =>
      perceptionClient.recentStills(roomId ?? "", signal),
    enabled: enabled && roomId !== null,
    structuralSharing: (old, next) => {
      const loaded = new Map(
        (old as RecentStill[] | undefined)?.map((s) => [s.reference, s]),
      );
      return (next as RecentStill[]).map((s) => {
        const before = loaded.get(s.reference);
        return before && s.signedAt - before.signedAt < RELINK_AFTER_MS
          ? before
          : s;
      });
    },
    staleTime: Infinity,
    gcTime: 0,
    retry: false,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
  });

/** Within brian's 5-minute cap. */
export const VIDEO_MS = 120_000;

/** Recorded footage does not change, so the `VIDEO_MS` from `from` is read once; null reads nothing. */
export const useRoomVideo = (
  sessionKey: string,
  roomId: string,
  from: number | null,
) =>
  useQuery({
    queryKey: perceptionKeys.video(sessionKey, roomId, from),
    queryFn:
      from === null
        ? skipToken
        : ({ signal }) =>
            perceptionClient.video(
              roomId,
              new Date(from).toISOString(),
              new Date(from + VIDEO_MS).toISOString(),
              signal,
            ),
    staleTime: Infinity,
    gcTime: 0,
    retry: false,
  });

/** A clinic emits about 70 room events an hour, and a door move lands up to 30 s after it happened. */
const EVENTS_POLL_MS = 10_000;

export const useRoomEvents = (
  sessionKey: string,
  roomId: string,
  active: boolean,
) =>
  useQuery({
    queryKey: perceptionKeys.events(sessionKey, roomId),
    queryFn: ({ signal }) => perceptionClient.roomEvents(roomId, signal),
    ...polling(active),
    refetchInterval: active ? EVENTS_POLL_MS : false,
  });

/** Entries change only when a tester records one, so this refetches on a write, not on a timer. */
export const useGroundTruth = (
  sessionKey: string,
  roomId: string,
  active: boolean,
) =>
  useQuery({
    queryKey: perceptionKeys.groundTruth(sessionKey, roomId),
    queryFn: ({ signal }) => perceptionClient.groundTruth(roomId, signal),
    enabled: active,
  });

export const useRecordGroundTruth = (sessionKey: string, roomId: string) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (body: GroundTruthIn) =>
      perceptionClient.recordGroundTruth(roomId, body),
    onSuccess: () =>
      queryClient.invalidateQueries({
        queryKey: perceptionKeys.groundTruth(sessionKey, roomId),
      }),
    meta: { action: "record what happened" },
  });
};

/** The correct drawer shows refusals beside their rows, so the global notice stays quiet. */
export const useRecordObservations = (sessionKey: string, roomId: string) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (body: ObservationsIn) =>
      perceptionClient.recordObservations(roomId, body),
    onSuccess: () =>
      queryClient.invalidateQueries({
        queryKey: perceptionKeys.phases(sessionKey),
      }),
    meta: { handled: true },
  });
};
